import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Marquee from "react-fast-marquee";
import { Sparkles, Mic, Image as ImageIcon, CalendarClock, TrendingUp, ArrowRight, Check, Volume2, Loader2, Square } from "lucide-react";
import { BrandWordmark } from "@/components/BrandWordmark";
import { FaYoutube, FaTiktok, FaInstagram } from "react-icons/fa";
import Examples from "@/components/Examples";
import { HowItWorksButton } from "@/components/HowItWorks";
import ReviewsWall from "@/components/ReviewsWall";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { CreateathonBanner } from "@/components/CreateathonBanner";
import CommunityGoal from "@/components/CommunityGoal";
import Seo from "@/components/Seo";
import { LANDING_NICHES } from "@/lib/niches";
import api from "@/lib/api";

function HearSampleButton() {
  const [state, setState] = useState(null);
  const ref = useRef(null);
  const toggle = async () => {
    if (state === "playing") {
      ref.current?.pause();
      setState(null);
      return;
    }
    setState("loading");
    try {
      const resp = await api.get("/public/sound-sample", { responseType: "blob" });
      const a = new Audio(URL.createObjectURL(resp.data));
      a.onended = () => setState(null);
      ref.current = a;
      await a.play();
      setState("playing");
    } catch {
      setState(null);
    }
  };
  return (
    <button
      onClick={toggle}
      data-testid="hear-sample-btn"
      title="10 seconds of AI voice + soundtrack — exactly what your videos sound like"
      className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-5 py-3 rounded-md text-sm font-semibold transition-colors"
    >
      {state === "loading" ? (
        <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
      ) : state === "playing" ? (
        <Square className="h-4 w-4 text-blue-400" />
      ) : (
        <Volume2 className="h-4 w-4 text-blue-400" />
      )}
      Hear a sample
    </button>
  );
}

const HERO_BG =
  "https://images.unsplash.com/photo-1567095751004-aa51a2690368?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBhYnN0cmFjdCUyMGxhbmRzY2FwZSUyMGRhcmt8ZW58MHx8fHwxNzgxNjMxNTMwfDA&ixlib=rb-4.1.0&q=85";

function RotatingWord() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % LANDING_NICHES.length), 2200);
    return () => clearInterval(t);
  }, []);
  const n = LANDING_NICHES[i];
  return (
    <Link
      key={n.v}
      to={`/register?niche=${n.v}`}
      data-testid="hero-rotating-word"
      title={`Start a ${n.label} channel — your first video topic is pre-filled`}
      className="inline-block text-blue-500 animate-word-swap hover:text-blue-400 underline decoration-blue-500/30 underline-offset-8 decoration-4 transition-colors"
    >
      {n.word}
    </Link>
  );
}

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => {
    if (!sessionStorage.getItem("vl_visit")) {
      sessionStorage.setItem("vl_visit", "1");
      api.post("/track/visit").catch(() => {});
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <CreateathonBanner />
      <Seo
        title="AI Faceless Video Studio"
        description="Videoileo generates AI scripts, lifelike voiceovers and cinematic visuals, then schedules a faceless video series to YouTube & TikTok — engineered to grow subscribers and customers."
        path="/"
      />
      {/* Nav */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-200 ${scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : ""}`}>
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <BrandWordmark className="h-12" />
          </div>
          <div className="flex items-center gap-3">
            <a href="#examples" data-testid="nav-examples-link" className="hidden sm:inline-block text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors">Examples</a>
            <Link to="/pricing" data-testid="nav-pricing-link" className="hidden sm:inline-block text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors">Pricing</Link>
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors">Sign in</Link>
            <Link to="/register" data-testid="nav-signup-link" className="text-sm bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,122,255,0.3)]">
              Start free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-40 pb-24 px-5">
        <div className="absolute inset-0 overflow-hidden">
          <img src={HERO_BG} alt="Cinematic abstract dark backdrop for the Videoileo faceless video studio" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs text-zinc-300 mb-6">
            <Sparkles className="h-3.5 w-3.5 text-blue-400" /> AI faceless video studio · script → voice → visuals
          </div>
          <h1 className="font-head text-5xl md:text-7xl tracking-tighter font-black leading-[0.95]">
            Build a faceless<br />
            <RotatingWord /> channel that <span className="text-blue-500">grows.</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Generate long-form scripts, studio voiceovers, and cinematic visuals — then schedule a whole series to YouTube, TikTok & Instagram. Engineered to win subscribers and customers.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3 px-2">
            <Link to="/register" data-testid="hero-cta" className="group bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-md font-semibold flex items-center gap-2 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,122,255,0.35)]">
              Start creating free <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/login" className="px-6 py-3 rounded-md font-semibold border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
              Sign in
            </Link>
            <HowItWorksButton variant="hero" />
            <HearSampleButton />
          </div>
          <div className="mt-8 flex items-center justify-center gap-6 text-zinc-500">
            <FaYoutube className="h-7 w-7 hover:text-red-500 transition-colors" />
            <FaTiktok className="h-6 w-6 hover:text-white transition-colors" />
            <FaInstagram className="h-7 w-7 hover:text-pink-500 transition-colors" />
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-6 border-y border-white/10 bg-[#0A0A0A]" aria-hidden="true">
        <Marquee speed={40} gradient gradientColor="#0A0A0A" gradientWidth={120}>
          {["FACELESS", "LONG FORM", "VOICEOVER", "AI VISUALS", "SCHEDULED SERIES", "GROW SUBS", "MONETIZE"].map((t) => (
            <span key={t} className="font-head text-4xl font-black text-stroke mx-8">{t}</span>
          ))}
        </Marquee>
      </div>

      {/* Welcome / explainer video */}
      <section className="py-16 px-5 border-b border-white/10" data-testid="welcome-video-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-3">Welcome to Videoileo</p>
          <h2 className="font-head text-2xl md:text-3xl font-black tracking-tight mb-3">What we do — in 45 seconds</h2>
          <p className="text-sm md:text-base text-zinc-400 max-w-xl mx-auto mb-8">
            This narrated intro was made entirely with Videoileo — the same AI script, voiceover and visuals your videos get.
          </p>
          <div className="rounded-xl overflow-hidden border border-white/10 shadow-[0_20px_60px_rgba(0,122,255,0.15)] bg-black">
            <video
              controls
              preload="none"
              playsInline
              poster="/welcome-poster.jpg"
              className="w-full aspect-video"
              data-testid="welcome-video"
            >
              <source src="/welcome-video.mp4" type="video/mp4" />
              <source src="/welcome-video.webm" type="video/webm" />
            </video>
          </div>
          <Link
            to="/register"
            data-testid="welcome-video-cta"
            className="inline-block mt-8 bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-md font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,122,255,0.35)]"
          >
            Make your first video free
          </Link>
        </div>
      </section>

      {/* Niche reel — pick a category, start a channel */}
      <div className="py-8 border-b border-white/10 bg-[#0A0A0A]" data-testid="niches-marquee">
        <Marquee speed={30} direction="right" pauseOnHover gradient gradientColor="#0A0A0A" gradientWidth={120}>
          {LANDING_NICHES.map((n) => (
            <Link
              key={n.v}
              to={`/register?niche=${n.v}`}
              data-testid={`niche-card-${n.v}`}
              className="group relative mx-3 block h-40 w-64 rounded-md overflow-hidden border border-white/10 hover:border-blue-500/60 transition-colors"
            >
              <img src={n.img} alt={`${n.label} faceless channel niche`} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
              <span className="absolute bottom-2.5 left-3 font-head text-sm font-bold text-white">{n.label}</span>
              <span className="absolute bottom-3 right-3 text-[10px] font-semibold text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity">Start this niche →</span>
            </Link>
          ))}
        </Marquee>
      </div>

      {/* Bento features */}
      <section className="py-24 px-5">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium mb-3">The full pipeline</p>
          <h2 className="font-head text-3xl md:text-5xl tracking-tight font-bold max-w-2xl mb-12">
            One studio. From idea to scheduled.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            <Feature className="md:col-span-8" icon={Sparkles} title="AI scripts that retain"
              desc="Claude writes hook-driven, retention-optimized long-form scripts broken into scenes — with SEO titles, descriptions, tags and growth tips baked in." />
            <Feature className="md:col-span-4" icon={Mic} title="Studio voiceover"
              desc="Lifelike ElevenLabs narration in dozens of voices." />
            <Feature className="md:col-span-4" icon={ImageIcon} title="Cinematic visuals in Full HD"
              desc="AI-generated b-roll for every scene, rendered in crisp 1080p." />
            <Feature className="md:col-span-8" icon={CalendarClock} title="Schedule a whole series"
              desc="Spin up recurring series and queue episodes to YouTube, TikTok & Instagram on a calendar built for consistency — the #1 driver of channel growth." />
          </div>
        </div>
      </section>

      {/* Video examples */}
      <Examples />

      {/* Community goal counter */}
      <CommunityGoal />

      {/* Growth band */}
      <section className="py-20 px-5 border-t border-white/10">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <TrendingUp className="h-10 w-10 text-blue-500 mb-5" />
            <h2 className="font-head text-3xl md:text-4xl font-bold tracking-tight mb-4">Optimized for subscribers & customers</h2>
            <ul className="space-y-3 text-zinc-400">
              {["Curiosity-gap titles & thumbnail text", "Hooks engineered for the first 3 seconds", "Built-in CTAs that convert viewers to customers", "Best-time-to-post recommendations per niche"].map((t) => (
                <li key={t} className="flex items-start gap-3"><Check className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="border border-white/10 rounded-md bg-[#121212] p-8">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6">Starts free</p>
            <div className="flex items-end gap-2 mb-1">
              <span className="font-head text-5xl font-black">Free</span>
              <span className="text-zinc-400 mb-1">trial · email only</span>
            </div>
            <p className="text-sm text-zinc-500 mb-6">Make a couple of full short videos before you upgrade — no card required.</p>
            <Link to="/register" data-testid="pricing-cta" className="block text-center bg-blue-600 hover:bg-blue-500 py-3 rounded-md font-semibold transition-colors">
              Claim your credits
            </Link>
          </div>
        </div>
      </section>

      <ReviewsWall />

      {/* Pricing CTA */}
      <section className="py-24 px-5 border-t border-white/10">
        <div className="max-w-5xl mx-auto border border-white/10 rounded-md bg-[#121212] p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium mb-3">Pricing</p>
            <h2 className="font-head text-3xl md:text-4xl tracking-tight font-bold mb-2">Start free. Scale when it works.</h2>
            <p className="text-zinc-400 max-w-md leading-relaxed">
              Free trial with just your email, then plans from <span className="text-white font-medium">$19/mo</span> —
              limited-time launch pricing.
            </p>
          </div>
          <Link
            to="/pricing"
            data-testid="landing-see-pricing"
            className="shrink-0 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-md font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,122,255,0.35)]"
          >
            See plans &amp; pricing <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 px-5 border-t border-white/10">
        <div className="max-w-3xl mx-auto">
          <NewsletterSignup />
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 px-5 text-center text-sm text-zinc-600">
        <p>© 2026 Videoileo · The faceless video studio.</p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs">
          <Link to="/pricing" data-testid="footer-pricing-link" className="text-zinc-500 hover:text-zinc-300 transition-colors">Pricing</Link>
          <Link to="/guides" data-testid="footer-guides-link" className="text-zinc-500 hover:text-zinc-300 transition-colors">Guides</Link>
          <Link to="/blog" data-testid="footer-blog-link" className="text-zinc-500 hover:text-zinc-300 transition-colors">Blog</Link>
          <Link to="/faq" data-testid="footer-faq-link" className="text-zinc-500 hover:text-zinc-300 transition-colors">FAQ</Link>
          <Link to="/compare" data-testid="footer-compare-link" className="text-zinc-500 hover:text-zinc-300 transition-colors">Compare</Link>
          <Link to="/contact" data-testid="footer-contact-link" className="text-zinc-500 hover:text-zinc-300 transition-colors">Contact</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="text-zinc-500 hover:text-zinc-300 transition-colors">Terms of Service</Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-zinc-500 hover:text-zinc-300 transition-colors">Privacy Policy</Link>
          <Link to="/acceptable-use" data-testid="footer-aup-link" className="text-zinc-500 hover:text-zinc-300 transition-colors">Acceptable Use</Link>
          <Link to="/legal" data-testid="footer-legal-link" className="text-zinc-500 hover:text-zinc-300 transition-colors">Content &amp; Copyright</Link>
        </div>
        <p className="mt-3 max-w-2xl mx-auto text-xs text-zinc-700 leading-relaxed">
          You are responsible for your content. Do not generate, upload, or publish copyrighted material you do not own
          or have a license to use.
        </p>
      </footer>
    </div>
  );
}

function Feature({ className = "", icon: Icon, title, desc }) {
  return (
    <div className={`border border-white/10 rounded-md bg-[#121212] p-8 hover:border-white/20 transition-colors duration-200 ${className}`}>
      <div className="h-11 w-11 rounded-md bg-blue-600/15 border border-blue-500/30 flex items-center justify-center mb-5">
        <Icon className="h-5 w-5 text-blue-400" />
      </div>
      <h3 className="font-head text-xl font-semibold mb-2">{title}</h3>
      <p className="text-zinc-400 leading-relaxed text-sm">{desc}</p>
    </div>
  );
}